import { CurrencySelect } from '@jurnee/common/src/components/CurrencySelect';
import { DatePicker } from '@jurnee/common/src/components/DatePicker';
import { FileInput } from '@jurnee/common/src/components/FileInput';
import { PriceInput } from '@jurnee/common/src/components/PriceInput';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { SecondaryButton } from '@jurnee/common/src/components/primitives/buttons/SecondaryButton';
import { Divider } from '@jurnee/common/src/components/primitives/Divider';
import { Drawer } from '@jurnee/common/src/components/primitives/Drawer';
import { FormControl } from '@jurnee/common/src/components/primitives/FormControl';
import { useToast } from '@jurnee/common/src/components/primitives/hooks/useToast';
import { Input } from '@jurnee/common/src/components/primitives/Input';
import { HStack } from '@jurnee/common/src/components/primitives/stack/HStack';
import { VStack } from '@jurnee/common/src/components/primitives/stack/VStack';
import { DOCUMENT_CONTENT_TYPES } from '@jurnee/common/src/constants/ContentTypes';
import { PartnerDocumentUpsertBody } from '@jurnee/common/src/dtos/partnersDocuments';
import { DEFAULT_UTC_TIMEZONE } from '@jurnee/common/src/entities/Address';
import { BookingJSON } from '@jurnee/common/src/entities/Booking';
import { Currency } from '@jurnee/common/src/entities/Currency';
import { PartnerDocumentJSON } from '@jurnee/common/src/entities/PartnerDocument';
import { convertToTimezone, getCurrentTimeZone } from '@jurnee/common/src/utils/dates';
import { createPartnerDocument, uploadPartnerDocument } from 'api/partnerDocuments';
import { cloneElement, HTMLAttributes, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getPartnerSelector } from 'store/partner/partner.selectors';

interface Props {
  bookingId: BookingJSON['id'];
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  onCreate(partnerDocument: PartnerDocumentJSON): void;
}

export function PartnerInvoiceDrawer(props: Props) {
  const toast = useToast();
  const { t } = useTranslation(['invoice', 'common']);

  const partner = useSelector(getPartnerSelector);

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [amount, setAmount] = useState<number | null>(null);
  const [taxAmount, setTaxAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [dueAt, setDueAt] = useState<Date | null>(null);
  const [documentReference, setDocumentReference] = useState<string | null>(null);

  const [partnerDocument, setPartnerDocument] = useState<File | null>(null);

  const isAmountInvalid = amount !== null && amount <= 0;
  const isTaxAmountInvalid = taxAmount !== null && taxAmount < 0;
  const isSaveDisabled = !amount || isAmountInvalid || isTaxAmountInvalid || !partnerDocument || !dueAt;

  function onOpen() {
    setIsOpen(true);
  }

  function onClose() {
    setIsOpen(false);
  }

  async function onSave() {
    if (!partner || !partnerDocument || !dueAt || !amount) {
      return;
    }
    setIsSaving(true);

    const body: Omit<PartnerDocumentUpsertBody, 'path'> = {
      amount,
      taxAmount: taxAmount ?? 0,
      currency,
      dueAt: convertToTimezone(dueAt, getCurrentTimeZone(), DEFAULT_UTC_TIMEZONE),
      documentReference: documentReference ?? null,
      filename: partnerDocument.name,
      size: partnerDocument.size,
      type: 'INVOICE'
    };

    try {
      const { path } = await uploadPartnerDocument(partner.id, props.bookingId, partnerDocument);
      const document = await createPartnerDocument(partner.id, props.bookingId, { ...body, path });

      onClose();
      props.onCreate(document);
      toast.success(t('drawer.toasts.success'));
    } catch(err: unknown) {
      toast.error(t('drawer.toasts.error'));
    }

    setIsSaving(false);
  }

  function onDateChange(date: Date | null) {
    if (!date) {
      return;
    }

    setDueAt(date);
  }

  return (
    <>
      { cloneElement(props.children, { onClick: onOpen }) }

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={t('drawer.title')}
        p={5}
        footer={
          <HStack justifyContent="space-between" w="100%">
            <SecondaryButton size="sm" colorScheme="pink" onClick={onClose}>
              { t('common:buttons.close') }
            </SecondaryButton>

            <PrimaryButton size="sm" colorScheme="teal" onClick={onSave} isLoading={isSaving} isDisabled={isSaveDisabled}>
              { t('common:buttons.save') }
            </PrimaryButton>
          </HStack>
        }
      >
        <VStack w="100%" spacing={5}>
          <FormControl label={t('common:fields.currency.label')}>
            <CurrencySelect value={currency} onChange={setCurrency} />
          </FormControl>

          <HStack w="100%" spacing={4}>
            <PriceInput
              label={t('drawer.form.amount.label')}
              placeholder={t('drawer.form.amount.placeholder')}
              currency={currency}
              defaultValue={amount ?? undefined}
              onChange={setAmount}
              isInvalid={isAmountInvalid}
              isRequired
            />

            <PriceInput
              label={t('drawer.form.taxAmount.label')}
              placeholder={t('drawer.form.taxAmount.placeholder')}
              currency={currency}
              defaultValue={taxAmount ?? undefined}
              onChange={setTaxAmount}
              isInvalid={isTaxAmountInvalid}
              isRequired
            />
          </HStack>

          <Divider />

          <FormControl id="dueDate" label={t('drawer.form.dueDate.label')} isRequired>
            <DatePicker
              popperPlacement="top"
              dateFormat="dd MMM yyyy"
              selected={dueAt}
              placeholderText={t('drawer.form.dueDate.placeholder')}
              onChange={onDateChange}
              inputProps={{ size: 'sm' }}
            />
          </FormControl>

          <FormControl label={t('drawer.form.reference.label')}>
            <Input
              size="sm"
              name="reference"
              value={documentReference || ''}
              placeholder={t('drawer.form.reference.placeholder')}
              onChange={(e) => setDocumentReference(e.target.value)}
            />
          </FormControl>

          <FormControl id="fileDocument" label={t('drawer.form.document.label')} isRequired>
            <FileInput
              label={t('common:fields.fileDocument.label')}
              sublabel={t('common:fields.fileDocument.documentTypes')}
              contentTypes={DOCUMENT_CONTENT_TYPES}
              imageMaxWidth={1200}
              onChange={setPartnerDocument}
              onRemove={() => setPartnerDocument(null)}
            />
          </FormControl>
        </VStack>
      </Drawer>
    </>
  );
}